import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ClassroomTeam,
  PortfolioAiVersion,
  PortfolioEntry,
  coalescePortfolioEntries,
  visibleAiCreations,
  portfolioRevealReadiness,
  portfolioEntryMatchesSlot,
  visiblePortfolioPhotos,
} from '../../classroom/types';
import { ClassroomPortfolioService } from '../../classroom/ClassroomPortfolioService';
import ExpandableRevealMedia from './ExpandableRevealMedia';
import AiGroupBoard, { AiGroupBoardHandle } from './AiGroupBoard';
import SparkleReveal, { RevealAnimationStyle } from './SparkleReveal';
import { videoEmbedUrl } from '../../classroom/aiCreation';
import { formatSlotDate, ResolvedSessionPlan } from '../../utils/sessionPlan';

type Step = 'picker' | 'papers' | 'guess' | 'reveal' | 'creation' | 'result';
const INTERACTIVE_TYPES = new Set(['game_link', 'claude_artifact', 'link', 'pdf', 'model_3d']);
const REVEAL_STAGE_MS = 6000; // ~2.7s countdown, ~1.5s reveal, ~1.8s result hold.

interface RevealFlowProps {
  squadId: string;
  plan: ResolvedSessionPlan;
  initialPlanIndex: number;
  recommendedPlanIndex?: number;
  teachingPlanIndex?: number;
  squadAvatarUrl?: string;
  teams: ClassroomTeam[];
  entries: PortfolioEntry[];
  onClose: () => void;
  initialEntryId?: string | null;
  replay?: boolean;
}

const posterFor = (gen: PortfolioAiVersion) => gen.posterUrl || (gen.type === 'image' ? gen.url : undefined);

function RevealCreation({ gen }: { gen: PortfolioAiVersion }) {
  if (gen.type === 'audio') return <audio src={gen.url} controls preload="metadata" aria-label={gen.caption || 'Audio creation'} />;
  if (gen.type === 'video' && gen.url) {
    const embedUrl = videoEmbedUrl(gen.url);
    if (embedUrl) {
      return <iframe src={embedUrl} title={gen.caption || 'AI video creation'} allow="autoplay; encrypted-media" allowFullScreen />;
    }
    return <video src={gen.url} poster={gen.posterUrl} controls autoPlay playsInline preload="metadata">Your browser cannot play this video.</video>;
  }
  if (gen.url && /^https?:\/\//i.test(gen.url) && INTERACTIVE_TYPES.has(gen.type || 'link')) {
    return <div className="cd-reveal-live-embed">
      <iframe
        src={gen.url}
        title={gen.caption || 'AI creation website'}
        allow="fullscreen; autoplay; clipboard-read; clipboard-write"
        sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-presentation allow-scripts allow-same-origin"
        allowFullScreen
      />
      <div className="cd-reveal-live-embed-bar">
        <small>If this content cannot be displayed here:</small>
        <a href={gen.url} target="_blank" rel="noreferrer">Open it ↗</a>
      </div>
    </div>;
  }
  const poster = posterFor(gen);
  return poster
    ? <img src={poster} alt={gen.caption || 'AI creation'} />
    : <div className="cd-reveal-result-fallback">{gen.caption || gen.type}</div>;
}

const RevealFlow: React.FC<RevealFlowProps> = ({
  squadId, plan, initialPlanIndex, recommendedPlanIndex, teachingPlanIndex, squadAvatarUrl, teams, entries, onClose, initialEntryId, replay = false,
}) => {
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(initialPlanIndex);
  const selectedSlot = plan.slots[selectedPlanIndex];
  const classEntries = useMemo(
    () => coalescePortfolioEntries(entries.filter((entry) => selectedSlot && portfolioEntryMatchesSlot(entry, selectedSlot))),
    [entries, selectedSlot]
  );
  const classOptions = useMemo(() => plan.slots.map((slot, index) => ({
    slot,
    index,
    entries: coalescePortfolioEntries(entries.filter((entry) => portfolioEntryMatchesSlot(entry, slot))),
  })).filter((option) => option.entries.some((entry) => visiblePortfolioPhotos(entry.photos).length)), [entries, plan.slots]);
  const groupBoardRef = useRef<AiGroupBoardHandle>(null);
  const [showGroups, setShowGroups] = useState(true);
  const [step, setStep] = useState<Step>(initialEntryId ? 'papers' : 'picker');
  const [entryId, setEntryId] = useState<string | null>(initialEntryId || null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [genIndex, setGenIndex] = useState(0);
  const [guess, setGuess] = useState('');
  const [saving, setSaving] = useState(false);
  const [isReplayRun, setIsReplayRun] = useState(replay);
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [animationStyle, setAnimationStyle] = useState<RevealAnimationStyle>(() => (localStorage.getItem('classroomRevealAnimation') as RevealAnimationStyle) || 'curtains');

  const entry = classEntries.find((item) => item.id === entryId) || null;
  const team = teams.find((item) => item.id === entry?.teamId);
  const hasGroups = !!entry?.aiGroups?.length;
  const gens = visibleAiCreations(entry?.aiVersions).filter(gen => !entry?.aiGroups?.some(g => g.id === gen.groupId));
  const gen = gens[genIndex];
  const photos = visiblePortfolioPhotos(entry?.photos);
  const sourcedPhotos = gen?.sourcePhotoIds?.length
    ? photos.filter((photo) => gen.sourcePhotoIds?.includes(photo.id))
    : [];
  const revealPhotos = sourcedPhotos.length ? sourcedPhotos : photos;
  const entryGuess = entry?.guess_text;

  useEffect(() => {
    if (entryId) setGuess(entryGuess || '');
  }, [entryId, entryGuess]);

  useEffect(() => {
    if (step !== 'reveal') return;
    const timer = window.setTimeout(() => setStep('creation'), REVEAL_STAGE_MS);
    return () => window.clearTimeout(timer);
  }, [step, genIndex]);

  const choose = (chosen: PortfolioEntry) => {
    setShowGroups(true);
    setEntryId(chosen.id);
    setPhotoIndex(0);
    setGenIndex(0);
    setGuess(chosen.guess_text || '');
    setIsReplayRun(chosen.status === 'revealed');
    setStep('papers');
  };

  const finishGen = async () => {
    if (!entry) return;
    if (genIndex < gens.length - 1) {
      setGenIndex((index) => index + 1);
      setPhotoIndex(0);
      setStep('papers');
      return;
    }
    setSaving(true);
    try {
      if (!isReplayRun) {
        await ClassroomPortfolioService.completeReveal({
          squadId, entryId: entry.id, photos, aiVersions: hasGroups ? gens : entry.aiVersions || [], guessText: guess,
        });
        setCompletedIds((ids) => ids.includes(entry.id) ? ids : [...ids, entry.id]);
      }
      if (initialEntryId) onClose();
      else {
        setEntryId(null);
        setStep('picker');
      }
    } catch (error) {
      console.error(error);
      void window.appAlert('Could not save this reveal. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (entry && hasGroups && showGroups) return <div className="cd-reveal-shell cd-reveal-shell--groups" role="dialog" aria-modal="true" aria-label="Reveal and Review">
    <header className="cd-reveal-head"><strong>{team?.name} · Reveal &amp; Review</strong><button type="button" onClick={async () => { if (await groupBoardRef.current?.flushNotes()) onClose(); }} aria-label="Close reveal">×</button></header>
    <main className="cd-group-reveal-stage"><AiGroupBoard presentation ref={groupBoardRef} key={entry.id} squadId={squadId} entry={entry} animationStyle={animationStyle} />
      <div className="cd-group-tools cd-group-reveal-navigation">{gens.length > 0 && <button type="button" className="cd-btn" onClick={async () => { if (!await groupBoardRef.current?.flushNotes()) return; setShowGroups(false); setStep('papers'); }}>Review other creations ({gens.length})</button>}
      <button type="button" className="cd-btn" onClick={async () => { if (!await groupBoardRef.current?.flushNotes()) return; if (initialEntryId) onClose(); else { setEntryId(null); setStep('picker'); } }}>Back to teams</button></div>
    </main>
  </div>;

  return (
    <div className="cd-reveal-shell" role="dialog" aria-modal="true" aria-label="Reveal and Review">
      <header className="cd-reveal-head">
        <strong>Reveal &amp; Review</strong>
        <button type="button" onClick={onClose} aria-label="Close reveal">×</button>
      </header>

      {step === 'picker' && <main className="cd-reveal-picker">
        <div>{squadAvatarUrl && <img className="cd-reveal-squad-avatar" src={squadAvatarUrl} alt="Squad avatar"/>}
          <p className="cd-reveal-kicker">Choose a team</p><h1>Who’s ready to reveal?</h1>
          <label className="cd-reveal-class-picker">
            <span>Revealing work from</span>
            <select value={selectedPlanIndex} onChange={(event) => {
              setSelectedPlanIndex(Number(event.target.value));
              setEntryId(null);
              setCompletedIds([]);
            }}>
              {classOptions.map(({ slot: option, index }) => <option key={`${option.index}-${option.date || option.levelId}`} value={index}>
                Class {option.index} · {option.levelTitle}{option.date ? ` · ${formatSlotDate(option.date)}` : ''}{index === recommendedPlanIndex ? ' — Recommended' : ''}
              </option>)}
            </select>
          </label>
          {selectedPlanIndex === recommendedPlanIndex && recommendedPlanIndex !== teachingPlanIndex && <p className="cd-reveal-guidance">Recommended: this is the most recent earlier class with work ready to reveal.</p>}
          {classOptions.length === 0 && <p className="cd-reveal-guidance">No class has portfolio work to reveal yet.</p>}
          <div className="cd-reveal-style-picker"><span>Reveal style</span>{([
            ['wipe','Wipe'],['spin','Spin in'],['curtains','Drumroll curtains'],['vortex','Magic vortex'],['pixels','Pixel build']
          ] as Array<[RevealAnimationStyle,string]>).map(([id,label]) => <button type="button" className={animationStyle === id ? 'active' : ''} key={id} onClick={() => { setAnimationStyle(id); localStorage.setItem('classroomRevealAnimation', id); }}>{label}</button>)}</div>
        </div>
        <div className="cd-reveal-team-list">
          {teams.filter((item) => !item.archived).map((item) => {
            const itemEntry = classEntries.find((candidate) => candidate.teamId === item.id);
            const readiness = completedIds.includes(itemEntry?.id || '')
              ? 'revealed'
              : portfolioRevealReadiness(itemEntry);
            if (readiness === 'hidden') return null;
            const hasGen = readiness === 'ready_to_reveal' || readiness === 'revealed';
            const revealed = readiness === 'revealed';
            return <button key={item.id} type="button" disabled={!hasGen}
              className={`cd-reveal-team ${revealed ? 'is-revealed' : ''}`}
              onClick={() => itemEntry && choose(itemEntry)}>
              <span className="cd-reveal-team-name">{item.avatarUrl&&<img src={item.avatarUrl} alt=""/>}{item.name}</span>
              <small>{revealed ? '✓ Revealed — tap to replay' : hasGen ? '✦ Ready to reveal' : 'Still cooking — next class!'}</small>
            </button>;
          })}
        </div>
      </main>}

      {entry && team && step === 'papers' && <main className="cd-reveal-stage">
        <p className="cd-reveal-kicker">{genIndex + 1} of {gens.length} creation{gens.length === 1 ? '' : 's'}</p>
        <h1 className="cd-reveal-team-title">{team.avatarUrl&&<img src={team.avatarUrl} alt=""/>}{team.name}’s idea</h1>
        <ExpandableRevealMedia key={revealPhotos[photoIndex]?.id} className="cd-reveal-paper-focus" label={`Paper ${photoIndex + 1}`} image>
          <img src={revealPhotos[photoIndex]?.url} alt={`Paper ${photoIndex + 1}`} />
        </ExpandableRevealMedia>
        {revealPhotos.length > 1 && <nav className="cd-reveal-paper-nav" aria-label="Paper navigation">
          <button type="button" disabled={photoIndex === 0} onClick={() => setPhotoIndex((index) => index - 1)}>← Previous</button>
          <span>{photoIndex + 1} / {revealPhotos.length}</span>
          <button type="button" disabled={photoIndex === revealPhotos.length - 1} onClick={() => setPhotoIndex((index) => index + 1)}>Next →</button>
        </nav>}
        {photoIndex === revealPhotos.length - 1 && <button className="cd-reveal-primary" type="button" onClick={() => setStep('guess')}>Start guessing</button>}
      </main>}

      {entry && team && step === 'guess' && <main className="cd-reveal-stage cd-reveal-guess">
        <ExpandableRevealMedia className="cd-reveal-paper-corner" label="Team paper" image>
          <img src={revealPhotos[photoIndex]?.url} alt="Team paper" />
        </ExpandableRevealMedia>
        <h1>What do you think the AI made?</h1>
        <h2>What will it look like?</h2>
        {!isReplayRun ? <input value={guess} onChange={(event) => setGuess(event.target.value)}
          placeholder="Optional: capture the team’s guess" aria-label="Team guess" /> :
          <p className="cd-reveal-saved-guess">{guess || 'The team made their guess out loud.'}</p>}
        <button className="cd-reveal-primary" type="button" onClick={() => setStep('reveal')}>Lock it in</button>
      </main>}

      {entry && team && gen && step === 'reveal' && <main className="cd-reveal-stage">
        <SparkleReveal style={animationStyle} paperUrl={revealPhotos[photoIndex]?.url} posterUrl={posterFor(gen)} title={gen.caption || `${team.name} creation`} />
      </main>}

      {entry && team && gen && (step === 'creation' || step === 'result') && <main className="cd-reveal-stage">
        <p className="cd-reveal-kicker">{step === 'creation' ? 'The AI creation' : 'The result'}</p><h1 className="cd-reveal-team-title">{team.avatarUrl&&<img src={team.avatarUrl} alt=""/>}{team.name}</h1>
        <div className={`cd-reveal-result ${step === 'creation' ? 'is-creation-only' : ''}`}>
          <div key="idea" hidden={step === 'creation'}><span>The idea</span>
            <ExpandableRevealMedia key={revealPhotos[photoIndex]?.id} label="Student paper" image>
              <img src={revealPhotos[photoIndex]?.url} alt="Student paper" />
            </ExpandableRevealMedia>
            {revealPhotos.length > 1 && <nav className="cd-reveal-paper-nav" aria-label="Paper navigation">
              <button type="button" disabled={photoIndex === 0} onClick={() => setPhotoIndex((index) => index - 1)}>← Previous</button>
              <span>{photoIndex + 1} / {revealPhotos.length}</span>
              <button type="button" disabled={photoIndex === revealPhotos.length - 1} onClick={() => setPhotoIndex((index) => index + 1)}>Next →</button>
            </nav>}
          </div>
          <div key="creation"><span>AI creation</span>
            <ExpandableRevealMedia label="AI creation" image={gen.type === 'image'}><RevealCreation gen={gen} /></ExpandableRevealMedia>
          </div>
        </div>
        <p className="cd-reveal-saved-guess"><b>Our guess:</b> {guess || 'Shared out loud'}</p>
        <div className="cd-reveal-actions">
          {gen.url && INTERACTIVE_TYPES.has(gen.type || 'link') && <a className="cd-reveal-secondary" href={gen.url} target="_blank" rel="noreferrer">Open it ↗</a>}
          {step === 'creation' ? <button className="cd-reveal-primary" type="button" onClick={() => setStep('result')}>Compare with the idea</button> :
          <button className="cd-reveal-primary" type="button" disabled={saving} onClick={() => void finishGen()}>{saving ? 'Saving…' : genIndex < gens.length - 1 ? 'Next creation' : 'Done'}</button>}
        </div>
      </main>}
    </div>
  );
};

export default RevealFlow;
